import { msg } from "@lingui/core/macro";
import {
  createCustomerMutation,
  createPromotionMutation,
  deleteCustomerMutation,
  deletePromotionMutation,
  formatMoney,
  getCustomersQuery,
  getPromotionsQuery,
  parseMoneyToCents,
  type PromotionKind,
  queryKeys,
} from "@comandero/shared";
import type { Customer, Organization, Promotion } from "@comandero/powersync";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import { createSignal, For, Show } from "solid-js";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { TextField } from "@/components/ui/text-field";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";

interface CustomersManagerProps {
  org: () => Organization | null;
}

export function CustomersManager(props: CustomersManagerProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();

  const [name, setName] = createSignal("");
  const [phone, setPhone] = createSignal("");
  const [promoName, setPromoName] = createSignal("");
  const [promoKind, setPromoKind] = createSignal<PromotionKind>("percent");
  const [promoValue, setPromoValue] = createSignal("");

  const orgId = (): string | null => props.org()?.id ?? null;

  const customers = createPowerSyncQuery<Customer>({
    queryKey: () => queryKeys.comandero.customers(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getCustomersQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const promotions = createPowerSyncQuery<Promotion>({
    queryKey: () => queryKeys.comandero.promotions(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getPromotionsQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  async function addCustomer(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const id = orgId();
    if (!d || !id || !name().trim()) {
      return;
    }
    await createCustomerMutation(d, id, name(), { phone: phone() || undefined });
    setName("");
    setPhone("");
  }

  async function removeCustomer(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await deleteCustomerMutation(d, id);
  }

  async function addPromotion(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const id = orgId();
    if (!d || !id || !promoName().trim()) {
      return;
    }
    // percent → basis points (10% → 1000); amount → cents.
    const value =
      promoKind() === "percent"
        ? Math.round(Number(promoValue() || "0") * 100)
        : parseMoneyToCents(promoValue());
    await createPromotionMutation(d, id, promoName(), promoKind(), value);
    setPromoName("");
    setPromoValue("");
  }

  async function removePromotion(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await deletePromotionMutation(d, id);
  }

  return (
    <div class="flex flex-col gap-8">
      {/* Customers */}
      <section>
        <h2 class="mb-3 text-lg font-semibold">{t(msg`Customers`)}</h2>
        <form class="mb-3 flex flex-wrap gap-2" onSubmit={addCustomer}>
          <TextField
            data-testid="customer-name-input"
            placeholder={t(msg`Customer name`)}
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
          />
          <TextField
            data-testid="customer-phone-input"
            placeholder={t(msg`Phone`)}
            inputmode="tel"
            value={phone()}
            onInput={(e) => setPhone(e.currentTarget.value)}
          />
          <Button data-testid="add-customer-btn" type="submit">
            {t(msg`Add customer`)}
          </Button>
        </form>
        <ul class="flex flex-col gap-2">
          <For each={customers.data}>
            {(c) => (
              <li
                class="flex items-center justify-between gap-3 rounded-md border border-border/40 px-3 py-2 text-sm"
                data-testid="customer-item"
                data-customer-id={c.id}
              >
                <span data-testid="customer-row-name">{c.name}</span>
                <span class="text-muted-foreground" data-testid="customer-row-points">
                  {c.points ?? 0} {t(msg`pts`)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="delete-customer-btn"
                  onClick={() => void removeCustomer(c.id)}
                >
                  {t(msg`Delete`)}
                </Button>
              </li>
            )}
          </For>
        </ul>
      </section>

      {/* Promotions */}
      <section>
        <h2 class="mb-3 text-lg font-semibold">{t(msg`Promotions`)}</h2>
        <form class="mb-3 flex flex-wrap gap-2" onSubmit={addPromotion}>
          <TextField
            data-testid="promo-name-input"
            placeholder={t(msg`Promotion name`)}
            value={promoName()}
            onInput={(e) => setPromoName(e.currentTarget.value)}
          />
          <Select
            data-testid="promo-kind-select"
            aria-label={t(msg`Promotion type`)}
            value={promoKind()}
            onChange={(v) => setPromoKind(v === "amount" ? "amount" : "percent")}
            options={[
              { value: "percent", label: t(msg`Percent (%)`) },
              { value: "amount", label: t(msg`Fixed amount`) },
            ]}
          />
          <TextField
            data-testid="promo-value-input"
            placeholder={promoKind() === "percent" ? t(msg`% off`) : t(msg`Amount off`)}
            inputmode="decimal"
            value={promoValue()}
            onInput={(e) => setPromoValue(e.currentTarget.value)}
          />
          <Button data-testid="add-promo-btn" type="submit">
            {t(msg`Add promotion`)}
          </Button>
        </form>
        <ul class="flex flex-col gap-2">
          <For each={promotions.data}>
            {(p) => (
              <li
                class="flex items-center justify-between gap-3 rounded-md border border-border/40 px-3 py-2 text-sm"
                data-testid="promo-item"
              >
                <span>{p.name}</span>
                <span class="text-muted-foreground">
                  <Show when={p.kind === "percent"} fallback={formatMoney(p.value)}>
                    {(p.value ?? 0) / 100}%
                  </Show>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="delete-promo-btn"
                  onClick={() => void removePromotion(p.id)}
                >
                  {t(msg`Delete`)}
                </Button>
              </li>
            )}
          </For>
        </ul>
      </section>
    </div>
  );
}
